// ============================================================
// THE COMPANY — dagre layout engine for the org chart
// ============================================================

import dagre from "dagre";
import type { Node, Edge } from "@xyflow/react";
import type { Role, Dependency, RoleNodeData } from "./types";

const NODE_WIDTH = 200;
const NODE_HEIGHT = 80;
const RANK_SEP = 80;
const NODE_SEP = 40;

/**
 * Computes React Flow nodes + edges from roles and dependencies using dagre.
 *
 * Two kinds of edges are produced:
 * 1. **Hierarchy edges** (parent -> child) — solid, gray, smoothstep
 * 2. **Dependency edges** — animated, dashed, labeled
 */
export function getLayoutedElements(
  roles: Role[],
  dependencies: Dependency[],
  conversationCounts: Record<string, number> = {},
): { nodes: Node<RoleNodeData>[]; edges: Edge[] } {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({
    rankdir: "TB",
    ranksep: RANK_SEP,
    nodesep: NODE_SEP,
  });

  // --- Add nodes -----------------------------------------------------------
  for (const role of roles) {
    g.setNode(role.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  }

  // --- Build set of valid role IDs ------------------------------------------
  const roleIds = new Set(roles.map((r) => r.id));

  // --- Add hierarchy edges (parent → child) to dagre -----------------------
  const hierarchyEdges: Edge[] = [];
  for (const role of roles) {
    if (role.parentId && roleIds.has(role.parentId)) {
      const edgeId = `hierarchy-${role.parentId}-${role.id}`;
      g.setEdge(role.parentId, role.id);
      hierarchyEdges.push({
        id: edgeId,
        source: role.parentId,
        target: role.id,
        type: "smoothstep",
        style: { stroke: "#3a3a4a", strokeWidth: 1.5 },
        animated: false,
      });
    }
  }

  // --- Add dependency edges to dagre (so they influence layout) -------------
  const dependencyEdges: Edge[] = [];
  for (const dep of dependencies) {
    if (!roleIds.has(dep.sourceId) || !roleIds.has(dep.targetId)) continue;
    g.setEdge(dep.sourceId, dep.targetId);
    dependencyEdges.push({
      id: `dep-${dep.id}`,
      source: dep.sourceId,
      target: dep.targetId,
      type: "default",
      label: dep.label ?? undefined,
      animated: true,
      style: {
        stroke: "#6366f1",
        strokeWidth: 1.5,
        strokeDasharray: "6 3",
      },
    });
  }

  // --- Run dagre layout -----------------------------------------------------
  dagre.layout(g);

  // --- Build React Flow nodes -----------------------------------------------
  const nodes: Node<RoleNodeData>[] = roles.map((role) => {
    const pos = g.node(role.id);
    return {
      id: role.id,
      type: "roleNode",
      position: {
        x: pos.x - NODE_WIDTH / 2,
        y: pos.y - NODE_HEIGHT / 2,
      },
      data: {
        role,
        conversationCount: conversationCounts[role.id] ?? 0,
        pendingActionItems: 0,
        lastConversationAt: null,
        dimmed: false,
      },
    };
  });

  return {
    nodes,
    edges: [...hierarchyEdges, ...dependencyEdges],
  };
}
